<template>
    <div
        class="app-header"
        :class="[
            `app-header--${variant}`,
            {
                'venue-mode': isVenueModeOn,
            },
        ]"
    >
        <a class="app-header-logo" @click.prevent="$router.push({ name: 'SplashScreen' })">
            <AppLogo :type="variant === 'dark' ? 'dark' : 'light'" />
        </a>
        <div class="app-header-actions">
            <div v-if="isVenueModeOn && selectedMainEventId" class="app-header-search">
                <div class="app-header-nav-items">
                    <div class="app-header-nav-item" @click="openSearchModal()">
                        <div class="app-header-nav-item-icon">
                            <AppIcon class="text-24" name="search" type="outlined" />
                        </div>
                        <div class="app-header-nav-item-label">
                            {{ t('lang_navigation.SEARCH') }}
                        </div>
                    </div>
                </div>
            </div>
            <nav v-else class="app-header-nav" role="navigation">
                <div class="app-header-nav-items" :style="menuItemsStyle">
                    <RouterLink
                        v-for="item in menu.items"
                        :key="item.order"
                        class="app-header-nav-item"
                        :to="item.route"
                    >
                        <div class="app-header-nav-item-icon">
                            <AppIcon class="text-24" :name="item.iconName" :type="item.iconType" />
                        </div>
                        <div class="app-header-nav-item-label">
                            {{ item.label }}
                        </div>
                        <div v-if="item.submenu" class="app-header-nav-item-submenu">
                            <RouterLink
                                v-for="subItem in item.submenu.items"
                                :key="subItem.order"
                                class="app-header-nav-item-submenu-item"
                                :to="subItem.route"
                            >
                                <div class="app-header-nav-item-submenu-item-label">
                                    {{ subItem.label }}
                                </div>
                            </RouterLink>
                        </div>
                    </RouterLink>
                </div>
            </nav>
        </div>
        <div class="app-header-profile" @click="isProfileDropdownOpened = !isProfileDropdownOpened">
            <div class="app-header-profile-item">
                <AppSessionCounter
                    ref="userProfile"
                    :profile-theme="variant === 'dark' ? 'dark' : 'light'"
                    :renew-modal-time-in-seconds-before-expiration="
                        AUTH_RENEW_MODAL_TIME_IN_SECONDS_BEFORE_EXPIRATION
                    "
                />
            </div>
            <div v-if="isProfileDropdownOpened" class="drop-menu-user-profile">
                <RouterLink
                    :to="{
                        name: 'UserProfile',
                    }"
                >
                    <AppIcon name="account_circle" class="m-r-sm" />{{
                        $t('lang_navigation.PROFILE')
                    }}
                </RouterLink>
                <RouterLink
                    v-if="enabledFeatures['venue_mode']"
                    :to="
                        isVenueModeOn
                            ? {
                                  name: 'Home',
                              }
                            : venueModeHomeRoute
                    "
                >
                    <AppIcon name="exit_to_app" class="m-r-sm" />{{
                        $t('lang_navigation.SWITCH_MODE')
                    }}
                </RouterLink>
                <a @click="openSessionModal()">
                    <AppIcon name="logout" class="m-r-sm" />{{ $t('lang_navigation.LOGOUT') }}
                </a>
            </div>
            <AppIcon class="dropdown-arrow" name="arrow_drop_down" />
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from '@/i18n';
import { loadAsyncComponent } from '@fifa-bsm/bo-frontend-shared/core';
import AppLogo from '@fifa-bsm/bo-frontend-shared/components/AppLogo/AppLogo';
import AppIcon from '@fifa-bsm/bo-frontend-shared/components/AppIcon/AppIcon';
import AppSessionCounter from '@fifa-bsm/bo-frontend-shared/components/AppSessionCounter/AppSessionCounter';
import {
    filterNavigationByPermissions,
    getNavigationItems,
} from '@/app/core/components/AppHeader/navigation';
import { useIsVenueMode } from '@/app/shared/composables/useIsVenueMode';
import { AUTH_RENEW_MODAL_TIME_IN_SECONDS_BEFORE_EXPIRATION } from '@/app/core/common/config.ts';

type Variant = 'light' | 'dark';

withDefaults(
    defineProps<{
        variant?: Variant;
    }>(),
    {
        variant: 'light',
    }
);

const store = useStore();
const router = useRouter();
const { t } = useI18n();

const isProfileDropdownOpened = ref(false);
const userProfile = ref<any>(null);

const enabledFeatures = computed(() => store.getters['App/enabledFeatures']);

const user = computed(() => store.getters['User/user']);
const permissions = computed(() => store.getters['User/userPermissions']);

const selectedMainEventId = computed(() => store.state.Routing.selectedMainEventId);

const isVenueModeOn = useIsVenueMode();

const menu = computed(() => {
    if (!user.value) {
        return { active: false, items: [] as any[] };
    }

    const navigationItems = filterNavigationByPermissions(
        getNavigationItems({
            $t: t,
            user: user.value,
            router,
        }),
        permissions.value,
        user.value
    );

    const applyRouteMetaParams = (routeApplied: any) => {
        routeApplied.query = {
            ...(routeApplied.query ?? {}),
            fromHeader: 'true',
        };
    };

    navigationItems.forEach((navigationItem: any) => {
        if (navigationItem.route) {
            applyRouteMetaParams(navigationItem.route);
        }

        (navigationItem.submenu?.items ?? []).forEach((nestedItem: any) => {
            if (nestedItem.route) {
                applyRouteMetaParams(nestedItem.route);
            }
        });
    });

    return {
        active: true,
        items: navigationItems,
    };
});

const menuItemsStyle = computed(
    () => `grid-template-columns: repeat(${menu.value.items.length}, 1fr)`
);

const venueModeHomeRoute = computed(() =>
    selectedMainEventId.value
        ? {
              name: 'VenueModeEventsList',
              params: {
                  mainEventId: selectedMainEventId.value,
              },
          }
        : {
              name: 'VenueModeMainEventsList',
          }
);

function openSessionModal() {
    isProfileDropdownOpened.value = false;
    userProfile.value?.openRenewSessionModal();
}

function openSearchModal() {
    store.dispatch('Backdrop/showComponent', {
        id: 'VenueModeQuickSearch',
        component: loadAsyncComponent(
            () =>
                import(
                    '@/app/modules/venueMode/search/components/QuickSearchModal/QuickSearchModal.vue'
                )
        ),
    });
}
</script>

<style lang="scss" scoped>
@use '@/app/styles/globals';

/* stylelint-disable no-descending-specificity */
.app-header {
    z-index: var(--zindex-header);
    display: grid;
    height: 72px;
    padding: 0 var(--spacing-xl);
    grid-column-gap: var(--spacing-xl);
    grid-template-columns: minmax(min-content, 280px) 1fr minmax(min-content, 280px);
    @media (width <= 1365px) {
        grid-column-gap: var(--spacing-md);
        grid-template-columns: min-content 1fr min-content;
    }
    @media (width <= 767px) {
        flex: 0 1 50px;
    }

    .app-header-logo {
        display: flex;
        align-items: center;
        cursor: pointer;

        .app-logo {
            height: 72px;

            :deep(img) {
                min-width: 100%;
            }
        }
    }

    .app-header-actions {
        display: flex;
        width: 100%;
        flex: 1;
        flex-grow: 1;
        justify-content: center;
    }

    .app-header-nav,
    .app-header-search {
        position: relative;
        z-index: var(--zindex-navigation);
        display: flex;
        padding: 0 10px;
        transition: all cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.3s;

        .app-header-nav-items {
            bottom: -1px;
            display: flex;
            width: 100%;
            padding: 0;
            column-gap: var(--spacing-lg);

            .app-header-nav-item {
                position: relative;
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 0 var(--spacing-md);
                cursor: pointer;
                font-family: var(--font-secondary);
                font-weight: 700;
                transition: border 0.3s;

                @media (width <= 992px) {
                    padding: 0 20px;
                }

                .app-header-nav-item-label {
                    display: flex;
                    overflow: hidden;
                    font-size: 16px;
                    letter-spacing: 0.06em;
                    text-align: center;
                    text-transform: uppercase;
                    white-space: nowrap;
                }

                .app-header-nav-item-icon {
                    margin-right: var(--spacing-sm);
                }

                .app-header-nav-item-submenu {
                    position: absolute;
                    top: calc(100% - 1px);
                    left: 0;
                    display: none;
                    width: 100%;
                    min-width: 175px;
                    border: 1px solid var(--color-gray-light);

                    .app-header-nav-item-submenu-item {
                        @include globals.primary-font(14px, var(--color-gray), 400, 24px);

                        display: block;
                        padding: var(--spacing-sm);
                        background-color: var(--color-white);

                        &:hover {
                            background-color: var(--color-gray-lightest);
                            color: var(--color-blue-medium);
                        }

                        &:active {
                            color: var(--color-blue-medium);
                        }
                    }
                }

                &:last-child {
                    margin-right: 0;
                }

                &:hover {
                    .app-header-nav-item-submenu {
                        display: block;
                    }
                }
            }
        }
    }

    .app-header-profile {
        position: relative;
        display: flex;
        width: 100%;
        align-items: center;
        justify-content: flex-end;

        .app-header-profile-items {
            display: flex;
            align-items: center;

            @media (width <= 1365px) {
                display: none;
            }
        }

        .app-header-profile-item {
            display: flex;
            width: 100%;
            align-items: center;
            justify-content: flex-end;
            border-radius: 4px;
            cursor: pointer;

            &:hover {
                color: var(--color-gray-light);
                text-decoration: none;
            }

            &:last-child {
                margin-right: 0;
            }

            .app-header-profile-item-icon {
                margin-right: var(--spacing-xs);
            }

            .app-header-profile-item-label {
                font-size: 15px;
                font-weight: 600;

                .app-session-counter {
                    font-size: 10px;
                }
            }
        }

        .drop-menu-user-profile {
            position: absolute;
            top: calc(100% - 1px);
            right: 0;
            width: 200px;
            border: 1px solid var(--color-gray-light);

            a {
                position: relative;
                display: flex;
                align-items: center;
                padding: 10px;
                background-color: var(--color-white);
                color: var(--color-gray);
                cursor: pointer;

                &:hover {
                    background-color: var(--color-gray-lightest);
                    color: var(--color-blue-medium);
                }
            }
        }
    }

    &.venue-mode {
        grid-template-columns: minmax(min-content, 280px) 1fr fit-content(280px);

        .app-header-actions {
            justify-content: flex-end;
        }
    }

    &.app-header--dark {
        border-bottom: 1px solid var(--color-gray-200);
        background: var(--color-night);

        .app-header-nav-item {
            color: var(--color-white);

            &:hover {
                color: var(--color-gray-light);
            }

            &.router-link-active {
                color: var(--color-gray-medium);
            }
        }

        .app-header-profile .app-header-profile-item {
            color: var(--color-white);
        }
    }

    &.app-header--light {
        border-bottom: 1px solid var(--color-gray-light);
        background: var(--color-white);

        .app-header-nav-item {
            color: var(--color-night);

            &:hover {
                color: var(--color-blue-medium);
            }

            &.router-link-active {
                color: var(--color-blue);
            }
        }

        .app-header-profile .app-header-profile-item {
            color: var(--color-night);
        }
    }

    :deep(.app-session-counter) {
        justify-content: end;
    }

    .dropdown-arrow {
        color: var(--color-night);
        cursor: pointer;
        font-size: 32px;
        transition: 0.2s all;
    }

    .drop-menu-user-profile + .dropdown-arrow {
        transform: rotate(-180deg);
    }
}
</style>
